"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter, usePathname } from "next/navigation";
import { motion } from "framer-motion";
import { useComponentTranslations } from "./LanguageProvider";

interface LoginPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToRegister: () => void;
}

export default function LoginPopup({ isOpen, onClose, onSwitchToRegister }: LoginPopupProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const router = useRouter();
  const pathname = usePathname();
  const { t } = useComponentTranslations("Login");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setInfo("");

    try {
      const result = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        if (result.error === "EMAIL_NOT_VERIFIED") {
          setError(t("emailNotVerified"));
        } else {
          setError(t("invalidCredentials"));
        }
      } else {
        // Redirect to the page where the popup was opened
        router.refresh();
        onClose();
      }
    } catch {
      setError(t("errorOccurred"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    signIn("google", { callbackUrl: pathname });
    onClose();
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError(t("enterEmailForReset"));
      setInfo("");
      return;
    }

    setIsResetLoading(true);
    setError("");
    setInfo("");

    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          locale: pathname?.split("/")[1] || "ru",
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.message === "SMTP is not configured. Password recovery is temporarily unavailable.") {
          setError(t("smtpNotConfigured"));
        } else {
          setError(data.message || t("errorOccurred"));
        }
        return;
      }

      setInfo(t("resetEmailSent"));
    } catch {
      setError(t("errorOccurred"));
    } finally {
      setIsResetLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4 glass-dark" role="dialog" aria-modal="true" aria-label="Login">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-md"
      >
        <div className="glass-form p-8 relative">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">{t("title")}</h1>
            <p className="text-white/60">{t("subtitle")}</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                {t("email")}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="w-full px-4 py-3 bg-white/15 border border-white/25 glass-rounded text-white placeholder-white/50 focus:outline-none focus:border-white/40 focus:bg-white/20 glass-transition"
                placeholder={t("email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
                {t("password")}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="w-full px-4 py-3 bg-white/15 border border-white/25 glass-rounded text-white placeholder-white/50 focus:outline-none focus:border-white/40 focus:bg-white/20 glass-transition"
                placeholder={t("password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            {info && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/20 p-3">
                <p className="text-sm text-emerald-200">{info}</p>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={isLoading || isResetLoading}
                className="text-sm text-white/70 underline transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isResetLoading ? t("sendingReset") : t("forgotPassword")}
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-white text-black font-medium glass-rounded glass-transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t("signingIn") : t("signIn")}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/25" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 glass-light text-white/60">{t("orContinueWith")}</span>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="w-full flex justify-center py-2 px-4 glass-light glass-hover glass-transition text-sm font-medium text-white/80"
              >
                {t("signInWithGoogle")}
              </button>
            </div>
          </div>

          <div className="text-center mt-6">
            <p className="text-white/60">
              {t("noAccount")}{" "}
              <button
                onClick={onSwitchToRegister}
                className="text-white hover:text-white/80 transition-colors underline"
              >
                {t("signUp")}
              </button>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
